import { Box, Button, Group, Stack } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { navigate } from "gatsby";
import React, { ReactNode } from "react";

import { Card, CardProps, Text, Title } from "@components";

import * as classes from "./CalloutCard.module.css";

interface CalloutCardProps extends Omit<CardProps, "title" | "content"> {
  title: ReactNode;
  content?: ReactNode;
  icon?: ReactNode;
  theme?: "light" | "dark";
  href?: string;
  onClick?: () => void;
}

export function CalloutCard(props: CalloutCardProps) {
  const {
    title,
    content,
    icon,
    theme = "light",
    href,
    onClick,
    ...cardProps
  } = props;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (href) {
      navigate(href);
    }
  };

  const isClickable = !!(href || onClick);

  return (
    <Card
      bg={theme === "dark" ? "layoutDark" : "layoutLight"}
      p={{ base: "xl", md: "xxl" }}
      h="100%"
      className={classes.card}
      style={{
        cursor: isClickable ? "pointer" : "default",
        position: "relative",
        ...cardProps.style,
      }}
      onClick={isClickable ? handleClick : undefined}
      {...cardProps}
    >
      <Group h="100%" gap={0} align="center">
        <Stack hiddenFrom="md">
          <Box>
            <Title
              order={3}
              size="h4"
              mb="md"
              c={theme === "dark" ? "fontLight" : "fontDark"}
            >
              {title}
            </Title>

            <Text
              size="xs"
              c={theme === "dark" ? "fontLightSecondary" : "fontDark"}
            >
              {content}
            </Text>
          </Box>
        </Stack>

        <Group align="flex-start" style={{ width: "100%" }} visibleFrom="md">
          {icon && (
            <Box mt={-10} ml={-10}>
              {icon}
            </Box>
          )}

          <Box style={{ flex: 1 }} pr={isClickable ? 66 : 0}>
            <Title
              order={3}
              mb="md"
              c={theme === "dark" ? "fontLight" : "fontDark"}
            >
              {title}
            </Title>

            <Text
              size="sm"
              c={theme === "dark" ? "fontLightSecondary" : "fontDark"}
            >
              {content}
            </Text>
          </Box>
        </Group>

        {isClickable && (
          <>
            <Button
              fullWidth
              variant={theme === "dark" ? "glass" : "glass-light"}
              size="sm"
              mt="lg"
              rightSection={<IconChevronRight />}
              styles={{ section: { marginRight: -20 } }}
              hiddenFrom="md"
            >
              See more
            </Button>

            <Button
              variant={theme === "dark" ? "glass" : "glass-light"}
              size="xs"
              p={0}
              w={66}
              miw={66}
              h="auto"
              radius="sm"
              className={classes.chevronButton}
              style={{
                position: "absolute",
                top: "calc(-1 * var(--mantine-spacing-xxl) + 4px)",
                right: "calc(-1 * var(--mantine-spacing-xxl) + 4px)",
                bottom: "calc(-1 * var(--mantine-spacing-xxl) + 4px)",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
              visibleFrom="md"
            >
              <IconChevronRight size={40} />
            </Button>
          </>
        )}
      </Group>
    </Card>
  );
}
